/**
 * Zustand store for conversation UI + identity state.
 *
 * Owns the two distinct identities the assistant runs on:
 *   - conversationId (UUIDv7): the DURABLE conversation identity. Minted at t=0,
 *     carried in the URL (?c=), and sent in every WS `settings` message so a
 *     reload deterministically resumes the SAME conversation. One conversation
 *     spans many WebSocket sessions.
 *   - sessionId (UUIDv4): the WebSocket TRANSPORT key. Changing it triggers a WS
 *     reconnect. Distinct from conversationId on purpose — a conversation outlives
 *     many sessions (reconnects, voice↔text switches, reloads). Do NOT unify them.
 *
 * React Query handles server state (conversation list, messages); this store holds
 * UI-only + identity state.
 *
 * Selector usage:
 *   const conversationId = useConversationStore(s => s.conversationId);
 */

import { create } from 'zustand';
import { v4 as uuidv4, v7 as uuidv7 } from 'uuid';
import type { PageContextPayload } from '@/lib/embed-bridge';
import { PageContextPhase } from '@/machines/connection/page-context';

export interface ConversationUIState {
  /** Durable client-generated conversation identity (UUIDv7, time-ordered).
   *  Sent in every WS settings message; mirrored to the URL (?c=). */
  conversationId: string;
  /** WebSocket session ID (UUIDv4). Changes trigger a WS reconnect. */
  sessionId: string;
  /** Whether the conversation history dropdown is open */
  historyDropdownOpen: boolean;
  /** Title of the active conversation (from server, for display) */
  activeConversationTitle: string | null;

  // ── Scroll-up history pagination ──
  /** Cursor (seq) for loading the next-older page; null = no older page known. */
  historyCursor: number | null;
  /** Whether older history pages exist beyond what's loaded. */
  historyHasMore: boolean;
  /** An older-page fetch is in flight. */
  historyLoading: boolean;
  /** The last older-page fetch failed (cursor kept; user can retry). */
  historyError: boolean;
  /** The backend signalled it couldn't replay prior history (HISTORY_REPLAY_FAILED).
   *  page.tsx observes this to immediately release a queued ?prompt= rather than
   *  waiting out the fallback timeout. */
  historyReplayFailed: boolean;
  /** The backend reset us off a foreign/not-found ?c= onto a fresh conversation.
   *  Drives a declarative inline notice. Deliberately NOT a one-shot toast: the
   *  reset triggers page.tsx's URL sync (history.replaceState; formerly a
   *  router.replace whose navigation dropped Sonner toasts) — a state-driven
   *  banner renders reliably regardless of URL-sync timing. */
  resetNotice: boolean;
  /** True only while a RESUME is awaiting its HISTORY_RESTORED replay.
   *  This is the INTENT signal that distinguishes "resuming (history expected
   *  → show the skeleton)" from "brand-new chat (nothing to load → land on
   *  LANDING, no skeleton)". Set by resumeConversation; cleared by the
   *  restore resolving (success or HISTORY_REPLAY_FAILED) and by every
   *  new/reset flow via RESET_PAGINATION. */
  expectingHistory: boolean;
  /** True only between an explicit "New" click (startNewConversation) and the
   *  connection layer consuming it. Sibling INTENT signal to expectingHistory:
   *  it distinguishes "user explicitly started a fresh chat (land on the empty
   *  CHAT surface — sidebar + starter grid, per design T6)" from "first page
   *  visit (land on LANDING)". Consumed + cleared by useAssistantConnection's
   *  session-change effect; every resume/reset flow clears it via
   *  RESET_PAGINATION. */
  freshChatIntent: boolean;
  /** Session-scoped last-known preview snippet per conversation id. Client-side
   *  stand-in for the backend `preview` list field: keeps a row's preview line
   *  stable while a conversation is loading (resume clears the in-memory
   *  transcript, so the live-derived preview vanishes mid-load without this).
   *  A server-provided preview always wins at merge time (see
   *  conversation-history/utils.applyPreviewCache). Never reset — it should
   *  survive resume/new-chat flows for the life of the page. */
  previewCache: Record<string, string>;

  /**
   * Embed page-context handshake (page-context-plan.md WS-E.3). `payload` is
   * the loader's full extract (from the host's EMBED_IN.PAGE_CONTEXT
   * postMessage — set once per page/navigation, independent of socket
   * lifecycle); `phase`/`requestId`/`missing` mirror the pure decision
   * machine's state (machines/connection/page-context.ts) and are owned by
   * useAssistantConnection's driver. `phase` resets to `idle` on every
   * socket close (see `resetPageContextPhase`) — `payload` does NOT reset,
   * so the next socket open can re-run the handshake against the same
   * extract. Always `null`/idle on the non-embedded route (nothing ever
   * sets it there).
   */
  pageContext: {
    payload: PageContextPayload | null;
    requestId: string | null;
    phase: PageContextPhase;
    missing: string[];
  };
}

export interface ConversationUIActions {
  toggleHistoryDropdown: () => void;
  setHistoryDropdownOpen: (open: boolean) => void;
  /** Start a fresh conversation: new durable conversationId + new session. */
  startNewConversation: () => void;
  /** Start a fresh conversation for a NEW site-entry ask (the "Ask Sara"
   *  ?prompt= popover) WITHOUT minting a new sessionId — see its doc comment
   *  below for why this must stay a distinct action from startNewConversation. */
  startNewConversationForEntry: () => void;
  /** Resume a past conversation: adopt its id as the durable conversationId and
   *  mint a new session so the WS reconnects onto it. */
  resumeConversation: (id: string, title?: string | null) => void;
  /** Adopt a conversationId from the URL (?c=) at mount, without minting a new
   *  one and without forcing a reconnect. No-op if it already matches. */
  initFromUrl: (id: string) => void;
  /** Adopt a server-minted conversationId mid-session (the backend reset us off a
   *  foreign/not-found ?c=). Keeps the CURRENT sessionId — the WS is already open
   *  on this conversation — so it must NOT reconnect; only the durable id + title +
   *  pagination reset. page.tsx's URL-sync effect then swaps ?c= to the new id. */
  resetConversation: (id: string) => void;
  /** Update the active conversation title (after server generates it) */
  setActiveConversationTitle: (title: string | null) => void;

  // ── Scroll-up history pagination ──
  /** Set the pagination cursor + has-more (from WS restore or an older-page fetch). */
  setHistoryPage: (cursor: number | null, hasMore: boolean) => void;
  setHistoryLoading: (loading: boolean) => void;
  setHistoryError: (error: boolean) => void;
  setHistoryReplayFailed: (failed: boolean) => void;
  /** Show/dismiss the foreign-?c= reset notice banner. */
  setResetNotice: (show: boolean) => void;
  /** Mark a resume's history replay as pending/resolved (drives the skeleton). */
  setExpectingHistory: (expecting: boolean) => void;
  /** Remember a conversation's latest preview snippet for this session. */
  cachePreview: (id: string, preview: string) => void;

  // ── Embed page-context handshake ──
  /** The host posted a new extract (initial `ready`, or a same-panel
   *  navigation) — always resets phase/requestId/missing to idle, since a
   *  new payload REPLACES whatever binding was in flight. */
  setPageContextPayload: (payload: PageContextPayload) => void;
  /** Driver-owned: mirror the decision machine's state after each
   *  transition (`nextPageContextAction`'s returned `state`). */
  setPageContextMachineState: (state: {
    phase: PageContextPhase;
    requestId: string | null;
    missing: string[];
  }) => void;
  /** Socket closed — phase resets to idle (the payload itself is untouched;
   *  see the field's doc comment). No-op if already idle. */
  resetPageContextPhase: () => void;
}

// Fresh pagination/restore state — unknown until a restore populates it.
// expectingHistory rides here so every flow that resets restore state
// (startNewConversation, resetConversation, initial) defaults to "no history
// expected"; resumeConversation overrides it to true AFTER the spread.
const RESET_PAGINATION = {
  historyCursor: null,
  historyHasMore: false,
  historyLoading: false,
  historyError: false,
  historyReplayFailed: false,
  expectingHistory: false,
  freshChatIntent: false,
} as const;

export type ConversationStore = ConversationUIState & ConversationUIActions;

export const useConversationStore = create<ConversationStore>((set) => ({
  // ── State ──
  // Minted client-side on store creation. page.tsx either adopts a ?c= from the
  // URL (reload/resume) or syncs this freshly-minted id into the URL (new visit).
  conversationId: typeof window !== 'undefined' ? uuidv7() : '',
  sessionId: typeof window !== 'undefined' ? uuidv4() : '',
  historyDropdownOpen: false,
  activeConversationTitle: null,
  resetNotice: false,
  previewCache: {},
  pageContext: {
    payload: null,
    requestId: null,
    phase: PageContextPhase.IDLE,
    missing: [],
  },
  ...RESET_PAGINATION,

  // ── Actions ──
  toggleHistoryDropdown: () =>
    set((s) => ({ historyDropdownOpen: !s.historyDropdownOpen })),

  setHistoryDropdownOpen: (open) => set({ historyDropdownOpen: open }),

  startNewConversation: () =>
    set({
      conversationId: uuidv7(),
      sessionId: uuidv4(),
      historyDropdownOpen: false,
      activeConversationTitle: null,
      resetNotice: false,
      ...RESET_PAGINATION,
      // AFTER the spread: an explicit "New" lands on the empty CHAT surface
      // (starter grid), not LANDING — see the field's doc comment.
      freshChatIntent: true,
    }),

  // Distinct from startNewConversation(): this one is invoked from page.tsx's
  // PRE-CONNECTION entry-intent layout effect, before useAssistantConnection's
  // own mount effect has opened this mount's first socket — there is no live
  // connection yet to force a reconnect on. Minting a new sessionId there
  // used to race that mount effect: CONNECT_REQUESTED read the (still stale,
  // not-yet-synced) sessionIdRef and opened socket #1 under the OLD id, then
  // the session-change effect noticed the bump a beat later and immediately
  // closed it to open socket #2 under the new one — a real connection-churn
  // bug (killing socket #1 mid-response drops the in-flight reply, and the
  // reconnect only restores persisted messages without re-triggering
  // generation). The current sessionId is safe to reuse here precisely
  // because a PROMPT entry only ever fires once per fresh mount, before any
  // socket exists — reusing it still yields exactly one connection, and its
  // settings frame reads conversationId fresh from the store at send time.
  // The backend accepts a reused session id with a new conversation id
  // cleanly: per-session state is rebuilt on every connect, and an unknown
  // client-minted conversation id takes the brand-new-conversation path.
  // Deliberately does NOT set
  // freshChatIntent — that flag exists only to survive a session-change
  // effect's setUIState/outbox-clear (see startNewConversation and
  // useAssistantConnection's session-change effect); since no session
  // changes here, nothing would ever flip it back to false, and page.tsx's
  // prompt-release effect would wait on it forever. sendText (called by
  // page.tsx's releasePrompt) already flips UIState LANDING→CHAT itself.
  //
  // The conversationId, by contrast, MUST be re-minted. This store is
  // module-level and survives client-side (SPA) navigations, so "the
  // store-creation mint is still virgin" only holds on a full page load.
  // The real-world counterexample (owner repro, test7 2026-07-21): topic
  // click → chat → X (router.push home, no reload) → second topic click.
  // Without a mint here, the second entry reused the FIRST conversation's
  // id and its message appended to the old thread instead of starting a
  // new one — breaking the "a ?prompt= entry is always a NEW ask" contract
  // documented at the page.tsx call site. Re-minting inside the layout
  // effect briefly lets render-1 passive effects run with the old id (one
  // redundant history.replaceState + fetch), which is harmless: the
  // ?prompt= reload loop this used to feed was fixed at its root by
  // switching the URL sync off router.replace onto history.replaceState.
  startNewConversationForEntry: () =>
    set({
      conversationId: uuidv7(),
      // Deliberately NO new sessionId — see above.
      historyDropdownOpen: false,
      activeConversationTitle: null,
      resetNotice: false,
      ...RESET_PAGINATION,
    }),

  resumeConversation: (id, title = null) =>
    set({
      conversationId: id,
      // New session ID triggers WS reconnection in page.tsx.
      sessionId: uuidv4(),
      historyDropdownOpen: false,
      activeConversationTitle: title,
      resetNotice: false,
      // Reset — the resumed conversation's first page (+ cursor) arrives via the
      // WS history_restored event after the reconnect.
      ...RESET_PAGINATION,
      // AFTER the spread: a resume IS expecting a history replay — the
      // conversation surface shows the skeleton until HISTORY_RESTORED (or
      // HISTORY_REPLAY_FAILED) clears this.
      expectingHistory: true,
    }),

  initFromUrl: (id) =>
    set((s) => (s.conversationId === id ? s : { conversationId: id })),

  resetConversation: (id) =>
    set({
      conversationId: id,
      // Deliberately NO new sessionId: the WS session is already live on this
      // (now reset) conversation; minting one would force a needless reconnect.
      activeConversationTitle: null,
      // Show the inline notice (declarative — survives the reset's navigation,
      // unlike a one-shot toast). Cleared on the next new/resume or by dismissal.
      resetNotice: true,
      ...RESET_PAGINATION,
    }),

  setActiveConversationTitle: (title) =>
    set({ activeConversationTitle: title }),

  setHistoryPage: (cursor, hasMore) =>
    set({ historyCursor: cursor, historyHasMore: hasMore }),
  setHistoryLoading: (loading) => set({ historyLoading: loading }),
  setHistoryError: (error) => set({ historyError: error }),
  setHistoryReplayFailed: (failed) => set({ historyReplayFailed: failed }),
  setResetNotice: (show) => set({ resetNotice: show }),
  setExpectingHistory: (expecting) => set({ expectingHistory: expecting }),

  cachePreview: (id, preview) =>
    set((s) =>
      s.previewCache[id] === preview
        ? s
        : { previewCache: { ...s.previewCache, [id]: preview } }
    ),

  // ── Embed page-context handshake ──
  setPageContextPayload: (payload) =>
    set({
      pageContext: {
        payload,
        requestId: null,
        phase: PageContextPhase.IDLE,
        missing: [],
      },
    }),

  setPageContextMachineState: ({ phase, requestId, missing }) =>
    set((s) => ({
      pageContext: { ...s.pageContext, phase, requestId, missing },
    })),

  resetPageContextPhase: () =>
    set((s) =>
      s.pageContext.phase === PageContextPhase.IDLE &&
      s.pageContext.requestId === null &&
      s.pageContext.missing.length === 0
        ? s
        : {
            pageContext: {
              ...s.pageContext,
              phase: PageContextPhase.IDLE,
              requestId: null,
              missing: [],
            },
          }
    ),
}));
